import { Icons, PRODUCT_ICON_MAP, classifyTraveler, formatCurrency } from "./bookingConstants";
import { SSR_CATEGORIES } from "./StepSSR";
import styles from "./AddBooking.module.css";

/* Look up a category by its code */
const findCat = (code) => SSR_CATEGORIES.find((c) => c.code === code);

const LivePreview = ({ data, bookingRef }) => {
  const customer = data._prefillCustomer || data.customer;
  const newCust = data.newCustomer;
  const custName =
    customer?.name ||
    [newCust.firstName, (newCust.lastName || "").toUpperCase()].filter(Boolean).join(" ") ||
    null;

  const totalSelling = Object.values(data.pricing).reduce(
    (s, p) => s + (p.sellingPrice || 0), 0
  );
  const totalCost = Object.values(data.pricing).reduce(
    (s, p) => s + (p.costPrice || 0), 0
  );
  const margin = totalSelling - totalCost;
  const adjustmentsTotal = (data.adjustments || []).reduce((sum, a) => {
    const val =
      a.mode === "Fixed"
        ? a.value || 0
        : (totalSelling * (a.value || 0)) / 100;
    return sum + (a.type === "Discount" ? -val : val);
  }, 0);

  const ssrItems = data.ssrItems || [];
  const ssrTotal = ssrItems.reduce(
    (s, i) => s + (i.price || 0) * (i.qty || 1), 0
  );

  /* Group SSR items by category code for compact display */
  const ssrByCategory = ssrItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <aside className={styles.preview}>

      {/* ── Header ── */}
      <div className={styles.previewHeader}>
        <span className={styles.previewIcon}>{Icons.eye}</span>
        <span className={styles.previewTitle}>Live Preview</span>
      </div>

      {/* ── Ref ── */}
      <div className={styles.previewRef}>
        <span className={styles.previewRefLabel}>Booking Ref: </span>
        <span className={styles.previewRefValue}>{bookingRef || "Pending"}</span>
      </div>

      {/* ── Details ── */}
      <div className={styles.previewSection}>
        <div className={styles.previewSecLabel}>Details</div>
        <div className={styles.previewRow}>
          <span>Type</span>
          <span className={styles.previewVal}>{data.bookingType || "—"}</span>
        </div>
        <div className={styles.previewRow}>
          <span>Customer</span>
          <span className={styles.previewVal}>{custName || "—"}</span>
        </div>
        <div className={styles.previewRow}>
          <span>Status</span>
          <span className={styles.previewVal}>{data.bookingStatus || "Pending"}</span>
        </div>
        <div className={styles.previewRow}>
          <span>Payment</span>
          <span className={styles.previewVal}>{data.paymentStatus || "Unpaid"}</span>
        </div>
      </div>

      {/* ── Products ── */}
      {data.products.length > 0 && (
        <div className={styles.previewSection}>
          <div className={styles.previewSecLabel}>
            Products ({data.products.length})
          </div>
          {data.products.map((p) => (
            <div key={p.id} className={styles.previewProduct}>
              <span className={styles.previewProductIcon}>
                {PRODUCT_ICON_MAP[p.type] || Icons.box}
              </span>
              <span className={styles.previewProductName}>{p.type}</span>
              <span className={`${styles.previewProductStatus} ${styles[`pvSt_${p.status}`]}`}>
                {p.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Travelers ── */}
      {data.travelers.length > 0 && (
        <div className={styles.previewSection}>
          <div className={styles.previewSecLabel}>
            Travelers ({data.travelers.length})
          </div>
          {data.travelers.map((t, i) => (
            <div key={i} className={styles.previewTraveler}>
              <span className={styles.previewTravelerName}>
                {[t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ") || `Traveler ${i + 1}`}
              </span>
              <span className={styles.previewTravelerType}>
                {classifyTraveler(t.dob)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Assignments ── */}
      {(data.assignments || []).length > 0 && (
        <div className={styles.previewSection}>
          <div className={styles.previewSecLabel}>Assignments</div>
          <div className={styles.previewAssignCount}>
            {data.assignments.length} link{data.assignments.length !== 1 ? "s" : ""} configured
          </div>
        </div>
      )}

      {/* ── SSR ── */}
      {ssrItems.length > 0 && (
        <div className={styles.previewSection}>
          <div className={styles.previewSecLabel}>
            Special Requests ({ssrItems.length})
          </div>

          {Object.entries(ssrByCategory).map(([catCode, items]) => {
            const cat = findCat(catCode);
            return (
              <div key={catCode} style={{ marginBottom: 7 }}>

                {/* Category heading row */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: 11,
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: cat?.bg || "var(--surface-sunken)",
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  >
                    {cat?.icon || "📋"}
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      color: cat?.color || "var(--text-tertiary)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {cat?.name || catCode}
                  </span>
                </div>

                {/* Items */}
                {items.map((item) => (
                  <div
                    key={item.itemId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingLeft: 25,
                      paddingTop: 2,
                      paddingBottom: 2,
                      gap: 6,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono','SF Mono',monospace",
                          fontSize: 9,
                          fontWeight: 700,
                          color: cat?.color || "var(--accent-amber)",
                          background: cat?.bg || "var(--amber-50)",
                          padding: "1px 5px",
                          borderRadius: 3,
                          flexShrink: 0,
                          letterSpacing: "0.03em",
                        }}
                      >
                        {item.code}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--text-primary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </span>
                      {(item.qty || 1) > 1 && (
                        <span style={{ fontSize: 10, color: "var(--text-tertiary)", flexShrink: 0 }}>
                          ×{item.qty}
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "'JetBrains Mono','SF Mono',monospace",
                        color: item.price === 0 ? "#059669" : "var(--text-primary)",
                        flexShrink: 0,
                      }}
                    >
                      {item.price === 0
                        ? "Free"
                        : `€${(item.price * (item.qty || 1)).toFixed(2)}`}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}

          {/* SSR subtotal */}
          {ssrTotal > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 8,
                paddingTop: 7,
                borderTop: "1px dashed var(--border-light)",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                SSR Total
              </span>
              <span
                style={{
                  fontFamily: "'JetBrains Mono','SF Mono',monospace",
                  fontSize: 12,
                  fontWeight: 800,
                  color: "var(--text-primary)",
                }}
              >
                €{ssrTotal.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Financials ── */}
      {(Object.keys(data.pricing).length > 0 || (data.adjustments || []).length > 0) && (
        <div className={styles.previewSection}>
          <div className={styles.previewSecLabel}>Financials</div>
          <div className={styles.previewFinRow}>
            <span>Selling</span>
            <span className={styles.previewFinVal}>{formatCurrency(totalSelling)}</span>
          </div>
          <div className={styles.previewFinRow}>
            <span>Cost</span>
            <span className={styles.previewFinVal}>{formatCurrency(totalCost)}</span>
          </div>
          {ssrTotal > 0 && (
            <div className={styles.previewFinRow}>
              <span>SSR</span>
              <span className={styles.previewFinVal}>{formatCurrency(ssrTotal)}</span>
            </div>
          )}
          {adjustmentsTotal !== 0 && (
            <div className={styles.previewFinRow}>
              <span>Adjustments</span>
              <span className={styles.previewFinVal}>{formatCurrency(adjustmentsTotal)}</span>
            </div>
          )}
          <div className={`${styles.previewFinRow} ${styles.previewFinMargin}`}>
            <span>Final Price</span>
            <span className={styles.previewFinVal}>
              {formatCurrency(totalSelling + ssrTotal + adjustmentsTotal)}
            </span>
          </div>
          <div className={`${styles.previewFinRow} ${styles.previewFinMargin}`}>
            <span>Margin</span>
            <span className={`${styles.previewFinVal} ${margin >= 0 ? styles.previewFinPos : styles.previewFinNeg}`}>
              {formatCurrency(margin)}
            </span>
          </div>
          <div className={styles.previewFinRow}>
            <span>Outstanding</span>
            <span className={styles.previewFinVal}>
              {formatCurrency(totalSelling + ssrTotal + adjustmentsTotal)}
            </span>
          </div>
        </div>
      )}

      {/* ── Notes ── */}
      {(data.notes || []).length > 0 && (
        <div className={styles.previewSection}>
          <div className={styles.previewSecLabel}>Notes ({data.notes.length})</div>
          <div className={styles.previewAssignCount}>
            {data.notes.length} note{data.notes.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}

    </aside>
  );
};

export default LivePreview;